import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, inject, ViewChild, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { BreadcrumbItem, SeoComponent } from '@shared/components';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CategoryService } from '@app/products/services/category.service';
import { delay, filter, map, take, tap } from 'rxjs/operators';
import { ProductService } from '@app/products/services/product.service';
import { ProductDescriptionService } from '@app/products/services/product-description.service';
import { combineLatest } from 'rxjs';
import { Product, ProductCategory } from '@shared/models';
import { OthersProductsService } from './services/others-products.service';
import { WindowService } from '@shared/window/window.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { ProductsSingleComponent } from '../../components/products-single/products-single.component';
import { ProductThumbnailComponent } from '../../../shared/components/product-thumbnail/product-thumbnail.component';
import { AsyncPipe } from '@angular/common';
import { LocalizePipe } from '../../../i18n/pipes/lozalize.pipe';
import { LocalizeRouterService, SeoAlternateLinksService } from '@app/i18n';

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CategoryService, ProductService, OthersProductsService, ProductDescriptionService],
    imports: [BreadcrumbComponent, ProductsSingleComponent, RouterLink, ProductThumbnailComponent, AsyncPipe, TranslatePipe, LocalizePipe],
})
export class ProductComponent extends SeoComponent implements OnInit, AfterViewInit, OnDestroy {
    private zone = inject(NgZone);
    private activatedRoute = inject(ActivatedRoute);
    private windowService = inject(WindowService);
    private categoryService = inject(CategoryService);
    private productService = inject(ProductService);
    private othersProductsService = inject(OthersProductsService);
    private productDescriptionService = inject(ProductDescriptionService);
    private router = inject(Router);
    private localizeRouterService = inject(LocalizeRouterService);
    private seoAlternateLinksService = inject(SeoAlternateLinksService);

    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationNext') swiperNavigationNextRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperNavigationPrev') swiperNavigationPrevRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;
    private swiper?: Swiper;

    breadcrumbs: BreadcrumbItem[] = [
        {
            name: 'NAVIGATION.MAIN.main_page',
            url: '/',
        },
        {
            name: 'NAVIGATION.MAIN.products',
            url: '/products',
        },
    ];

    readonly otherProducts$ = this.othersProductsService.products$.pipe(filter((items) => items !== null));

    readonly categorySlug = this.activatedRoute.snapshot.params['slug'];
    readonly category$ = this.categoryService.category$;
    readonly product$ = this.productService.product$;
    readonly error$ = this.productService.error$;

    otherProductsCount$ = this.otherProducts$.pipe(map((products: Product[]) => ({ count: products.length })));

    breadcrumbs$ = combineLatest([this.category$, this.product$.pipe(filter((product) => product !== null))]).pipe(
        tap(() => {
            this.windowService.scrollTo();
        }),
        map(([category, product]: [ProductCategory | null, Product]) => {
            if (product.categories && product.categories.length > 0) {
                this.othersProductsService.dispatch(product.categories.map((category) => category.slug));
            }
            if (category) {
                return [
                    ...this.breadcrumbs,
                    {
                        name: category.name,
                        url: '/products/category/' + category.slug,
                    },
                    {
                        name: product.name,
                        url: null,
                    },
                ];
            } else {
                return [
                    ...this.breadcrumbs,
                    {
                        name: product.name,
                        url: null,
                    },
                ];
            }
        }),
    );

    override ngOnInit(): void {
        super.ngOnInit();

        this.windowService.scrollTop();

        this.subscriptions$.add(
            this.activatedRoute.params
                .pipe(
                    map((params) => params['productId']),
                    tap((productId) => {
                        this.productService.dispatch(productId);
                        // The same product is also reachable under category/:slug/:productId —
                        // canonicalize (and hreflang) to the all/:productId variant.
                        this.seoAlternateLinksService.setCanonicalPath(this.canonicalPath(productId));
                    }),
                )
                .subscribe(),
        );

        this.productDescriptionService.getProductSeoDescription();
        this.productDescriptionService.seoDescription$.pipe(tap((value) => this.seoDescription$.next(value))).subscribe();

        if (this.categorySlug) {
            this.categoryService.dispatch(this.categorySlug);
        }
    }

    override ngOnDestroy(): void {
        this.swiper?.destroy();
        this.othersProductsService.destroy();
        this.seoAlternateLinksService.setCanonicalPath(null);
        super.ngOnDestroy();
    }

    private canonicalPath(productId: string): string {
        return this.router.createUrlTree(this.localizeRouterService.translateRoute(['/products', 'all', productId])).toString();
    }

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.otherProducts$
                .pipe(
                    take(1),
                    delay(0),
                    tap(() => this.initSwiper()),
                )
                .subscribe();
        }
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Navigation],
                spaceBetween: 36,
                navigation: {
                    nextEl: this.swiperNavigationNextRef.nativeElement,
                    prevEl: this.swiperNavigationPrevRef.nativeElement,
                },
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    320: {
                        slidesPerView: 1.5,
                    },
                    450: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 3,
                    },
                    992: {
                        slidesPerView: 4,
                    },
                    1200: {
                        slidesPerView: 5,
                    },
                },
            });
        });
    }
}
